import styled, { css } from 'styled-components'

import { media } from '../../../utils/mediaQuery'
import { ModalVariant } from './Modal.types'

export const ModalOverlay = styled.div<{
  $isOpen: boolean
  $variant?: ModalVariant
}>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  z-index: ${({ theme }) => theme.zIndex.popover};

  /* Default centered variant */
  ${({ $variant }) =>
    $variant !== 'bottom-sheet' &&
    css`
      align-items: center;
      justify-content: center;
      padding: 16px;
    `}

  /* Bottom sheet variant - align to bottom */
  ${({ $variant }) =>
    $variant === 'bottom-sheet' &&
    css`
      align-items: flex-end;
      justify-content: center;
      padding: 0;
    `}
`

export const ModalContent = styled.div<{
  $width?: string
  $maxWidth?: string
  $fullScreen?: boolean
  $isOpen: boolean
  $variant?: ModalVariant
}>`
  background: ${({ theme }) => theme.colors.background.primary};
  box-shadow: ${({ theme }) => theme.shadows.lg};
  position: relative;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  /* Default variant styles */
  ${({ $variant, $width, $maxWidth, theme }) =>
    $variant !== 'bottom-sheet' &&
    css`
      border-radius: ${theme.borderRadius.large};
      width: ${$width || 'auto'};
      max-width: ${$maxWidth || '480px'};
      max-height: 90vh;
      max-height: 90dvh;
    `}

  /* Bottom sheet variant styles */
  ${({ $variant, $maxWidth, theme }) =>
    $variant === 'bottom-sheet' &&
    css`
      width: 100%;
      max-width: ${$maxWidth || '100%'};
      max-height: 90vh;
      max-height: 90dvh;
      border-radius: ${theme.borderRadius.xlarge} ${theme.borderRadius.xlarge} 0
        0;
      /* Safe area padding for notched devices */
      padding-bottom: env(safe-area-inset-bottom);
    `}

  ${({ $fullScreen }) =>
    $fullScreen &&
    css`
      width: 100%;
      height: 100%;
      max-width: 100%;
      max-height: 100%;
      border-radius: 0;
    `}

  /* Default mobile behavior (full-screen) */
  ${media.tabletDown} {
    ${({ $variant }) =>
      $variant !== 'bottom-sheet' &&
      css`
        width: 100%;
        max-width: 100%;
        height: 100vh;
        height: 100dvh;
        max-height: 100%;
        border-radius: 0;
      `}
  }
`

export const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  transition: ${({ theme }) => theme.transitions.default};
  z-index: 1;
  /* Ensure 44px minimum touch target */
  min-width: ${({ theme }) => theme.touchTargets.minimum};
  min-height: ${({ theme }) => theme.touchTargets.minimum};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
  }

  &:focus {
    outline: none;
    box-shadow: ${({ theme }) => theme.shadows.focus};
  }

  /* Disable hover effects on touch devices */
  @media (hover: none) {
    &:hover {
      background: none;
    }

    &:active {
      background: ${({ theme }) => theme.colors.background.secondary};
    }
  }
`

/**
 * Drag handle for bottom-sheet variant
 * Provides visual affordance for swipe-to-dismiss
 */
export const DragHandle = styled.div`
  width: 28px;
  height: 3px;
  background: ${({ theme }) => theme.colors.border.default};
  border-radius: 2px;
  margin: 10px auto 6px;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
`
